import Configuration from "./../../config";
import {
  SlackMessage,
  IntercomSlackAction,
  SessionInAdminConsoleSlackAction,
  sendMessageToSlack,
} from "./../slackIntegration";
import { saveEvent } from "./../../services/sessions";
import DeviceTypes from "../../model/DeviceTypes";
import { isUserTeacher, getSessionUser } from "./../authUtil";

// The various types of Lesson session events the server accepts.
export const LessonSessionEventTypes = Object.freeze({
  ERROR_MEDIA_AUDIOVIDEO_PROBLEMS: "error-media-av-problems",
  WARNING_MEDIA_AUDIO_NOT_FOUND: "warning-media-audio-not-found",
  WARNING_MEDIA_AUDIO_NOT_ALLOWED: "warning-media-audio-not-allowed",
  WARNING_MEDIA_AUDIO_NOT_DETECTED: "warning-media-audio-not-detected",
  WARNING_MEDIA_VIDEO_NOT_FOUND: "warning-media-video-not-found",
  WARNING_MEDIA_VIDEO_NOT_ALLOWED: "warning-media-video-not-allowed",
  WARNING_MEDIA_VIDEO_NOT_DETECTED: "warning-media-video-not-detected",
  WARNING_MEDIA_UNSUPPORTED_BROWSER: "warning-media-unsupported-browser",
  WARNING_USER_OTHER_LATE: "warning-user-other-late",
  SUCCESS_USER_BOTH_CONNECTED: "success-user-both-connected",
  INFO_NETWORK_RECONNECT: "info-network-reconnect",
  INFO_MEDIA_VIDEO_DISABLED: "info-media-video-disabled",
  INFO_MEDIA_VIDEO_ENABLED: "info-media-video-enabled",
  INFO_MEDIA_VIDEO_DEGRADING: "info-media-video-degrading",
  INFO_MEDIA_VIDEO_IMPROVING: "info-media-video-improving",
});

/**
 * Base class for admin auto notifications. More info is [here](https://docs.google.com/document/d/17u79IescDHeyWMcGKeEUl0Xu9HbmltI0G4fplIjgaPU/edit).
 */
export class AdminNotification {
  constructor(message) {
    this.message = message;
  }

  send() {
    if (Configuration.SLACK.enable) {
      sendMessageToSlack(this.message);
    } else {
      // eslint-disable-next-line
      console.log(this.message);
    }
  }
}

export class SessionNotification extends AdminNotification {
  constructor(message, lessonEvent) {
    super(message);
    this.lessonEvent = lessonEvent;
  }

  send() {
    saveEvent(
      this.lessonEvent,
      () => {
        // Do nothing, server relays the message.
      },
      () => {
        super.send();
      }
    );
  }
}

export class SessionSuccessNotification extends SessionNotification {
  constructor(pretext, title, recommendation, actions, lessonEvent) {
    super(
      new SlackMessage(
        "SESSION SUCCESS",
        pretext,
        title,
        "A new session success",
        recommendation,
        ":party_parrot:",
        "#00FF00",
        actions
      ),
      lessonEvent
    );
  }
}

export class SessionWarningNotification extends SessionNotification {
  constructor(pretext, title, recommendation, actions, lessonEvent) {
    super(
      new SlackMessage(
        "SESSION WARNING",
        pretext,
        title,
        "A new session warning",
        recommendation,
        ":warning:",
        "#FFC107",
        actions
      ),
      lessonEvent
    );
  }
}

export class SessionErrorNotification extends SessionNotification {
  constructor(pretext, title, recommendation, actions, lessonEvent) {
    super(
      new SlackMessage(
        "SESSION ERROR",
        pretext,
        title,
        "A new session error",
        recommendation,
        ":sos:",
        "#FF0000",
        actions
      ),
      lessonEvent
    );
  }
}

export class SessionInfoNotification extends SessionNotification {
  constructor(pretext, title, recommendation, actions, lessonEvent) {
    super(
      new SlackMessage(
        "SESSION INFO",
        pretext,
        title,
        "A new session info",
        recommendation,
        ":information_source:",
        "#0000FF",
        actions
      ),
      lessonEvent
    );
  }
}

export class GeneralWarningNotification extends AdminNotification {
  constructor(pretext, title, recommendation, actions) {
    super(
      new SlackMessage(
        "GENERAL WARNING",
        pretext,
        title,
        "A new general warning",
        recommendation,
        ":warning:",
        "#FFC107",
        actions
      )
    );
  }
}

function getUsersDetails(lesson) {
  const currentUser = getSessionUser();
  if (isUserTeacher(currentUser)) {
    return {
      user: {
        profile: currentUser.profile,
        role: "teacher",
      },
      other: {
        profile: lesson.user,
        role: "student",
      },
    };
  } else {
    return {
      user: {
        profile: currentUser.profile,
        role: "student",
      },
      other: {
        profile: lesson.instructor,
        role: "teacher",
      },
    };
  }
}

function getDeviceDetailsByType(deviceType) {
  if (deviceType === DeviceTypes.VIDEO) {
    return {
      name: "camera",
      action: "see",
    };
  } else {
    return {
      name: "microphone",
      action: "hear",
    };
  }
}

function getIdSubstring(id) {
  return id ? id.substring(0, 8) : "";
}

function getSessionWarningDefaultPretext(lesson) {
  return `The session (${getIdSubstring(lesson.id)}) between ${lesson.user.first_name} (student) and ${lesson.instructor.first_name} (teacher) needs your attention.`;
}

function getSessionWarningDefaultRecommendation() {
  return "Notify the teacher of the condition";
}

function getSessionInfoDefaultPretext(lesson) {
  return `Information for session (${getIdSubstring(lesson.id)}) between ${lesson.user.first_name} (student) and ${lesson.instructor.first_name} (teacher).`;
}

function getGeneralWarningDefaultPretext(user) {
  return `Information about user (${getIdSubstring(user.id)}) ${user.profile.first_name} (${user.profile.email}).`;
}

function getDefaultSessionNotificationActions(lesson) {
  return [
    new IntercomSlackAction(lesson.user.id, lesson.user.first_name, "student"),
    new IntercomSlackAction(
      lesson.instructor.id,
      lesson.instructor.first_name,
      "teacher"
    ),
    new SessionInAdminConsoleSlackAction(lesson.id),
  ];
}

export class NotFoundDeviceSessionNotification extends SessionWarningNotification {
  constructor(lesson, deviceType) {
    const users = getUsersDetails(lesson);
    const device = getDeviceDetailsByType(deviceType);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType:
        deviceType === DeviceTypes.AUDIO
          ? LessonSessionEventTypes.WARNING_MEDIA_AUDIO_NOT_FOUND
          : LessonSessionEventTypes.WARNING_MEDIA_VIDEO_NOT_FOUND,
      platform: lesson.platform,
    };

    super(
      getSessionWarningDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) doesn’t have a ${device.name}, ${users.other.profile.first_name} (${users.other.role}) won’t be able to ${device.action} him/her.`,
      getSessionWarningDefaultRecommendation(),
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class NotAllowedDeviceSessionNotification extends SessionWarningNotification {
  constructor(lesson, deviceType) {
    const users = getUsersDetails(lesson);
    const device = getDeviceDetailsByType(deviceType);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType:
        deviceType === DeviceTypes.AUDIO
          ? LessonSessionEventTypes.WARNING_MEDIA_AUDIO_NOT_ALLOWED
          : LessonSessionEventTypes.WARNING_MEDIA_VIDEO_NOT_ALLOWED,
      platform: lesson.platform,
    };

    super(
      getSessionWarningDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) didn’t allow Talaera to have access to his/her ${device.name}, ${users.other.profile.first_name} (${users.other.role}) won’t be able to ${device.action} him/her.`,
      getSessionWarningDefaultRecommendation(),
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class NotReadableDeviceSessionNotification extends SessionWarningNotification {
  constructor(lesson, deviceType) {
    const users = getUsersDetails(lesson);
    const device = getDeviceDetailsByType(deviceType);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType:
        deviceType === DeviceTypes.AUDIO
          ? LessonSessionEventTypes.WARNING_MEDIA_AUDIO_NOT_DETECTED
          : LessonSessionEventTypes.WARNING_MEDIA_VIDEO_NOT_DETECTED,
      platform: lesson.platform,
    };

    super(
      getSessionWarningDefaultPretext(lesson),
      `${users.user.profile.first_name}’s (${users.user.role}) ${device.name} can’t be detected, it’s possibly in use of other application. ${users.other.profile.first_name} (${users.other.role}) won’t be able to ${device.action} him/her.`,
      getSessionWarningDefaultRecommendation(),
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class BothDevicesProblemSessionNotification extends SessionErrorNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.ERROR_MEDIA_AUDIOVIDEO_PROBLEMS,
      platform: lesson.platform,
    };

    super(
      getSessionWarningDefaultPretext(lesson),
      `We encountered problems with both ${users.user.profile.first_name}’s (${users.user.role}) devices, he/she can be moved to Conversation but ${users.other.profile.first_name} (${users.other.role}) won’t be able to either hear or see ${users.user.profile.first_name}.`,
      getSessionWarningDefaultRecommendation(),
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class SeveralReconnectionsSessionNotification extends SessionInfoNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.INFO_NETWORK_RECONNECT,
      platform: lesson.platform,
    };

    super(
      getSessionInfoDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) is probably experiencing drops in network connectivity or moving between network interfaces such as changing his/her Wi-Fi.`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class VideoDisabledSessionNotification extends SessionInfoNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.INFO_MEDIA_VIDEO_DISABLED,
      platform: lesson.platform,
    };

    super(
      getSessionInfoDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) can no longer see a video of ${users.other.profile.first_name} (${users.other.role}) because one of the participants has a poor internet connection.`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class VideoDisableWarningSessionNotification extends SessionInfoNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.INFO_MEDIA_VIDEO_DEGRADING,
      platform: lesson.platform,
    };

    super(
      getSessionInfoDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) won’t be able to see ${users.other.profile.first_name} (${users.other.role}). ${users.user.profile.first_name}’s subscribed video will be disabled if quality degrades more. It’s possible ${users.other.profile.first_name} has problems with his/her network.`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class VideoDisableWarningLiftedSessionNotification extends SessionInfoNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.INFO_MEDIA_VIDEO_IMPROVING,
      platform: lesson.platform,
    };

    super(
      getSessionInfoDefaultPretext(lesson),
      `${users.user.profile.first_name}’s (${users.user.role}) subscribed video won’t be disabled since quality has improved.`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class VideoEnabledSessionNotification extends SessionInfoNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.INFO_MEDIA_VIDEO_ENABLED,
      platform: lesson.platform,
    };

    super(
      getSessionInfoDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) is able to see ${users.other.profile.first_name} (${users.other.role}) again. ${users.user.profile.first_name}’s subscribed video was enabled since the stream quality has improved.`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class UnsupportedBrowserSessionNotification extends SessionWarningNotification {
  constructor(lesson, browserName, userOS) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.WARNING_MEDIA_UNSUPPORTED_BROWSER,
      platform: lesson.platform,
    };

    super(
      getSessionWarningDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) entered the session from unsupported browser (${browserName}, ${userOS}).`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class OtherUserLateSessionNotification extends SessionWarningNotification {
  constructor(lesson, lastSeen) {
    const users = getUsersDetails(lesson);
    const lastSeenInfo = lastSeen ? ", last seen " + lastSeen : "";
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.WARNING_USER_OTHER_LATE,
      platform: lesson.platform,
    };

    super(
      getSessionWarningDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) is waiting for too long. Is ${users.other.profile.first_name} (${users.other.role}${lastSeenInfo}) in the system already? Has he/she missed the session?`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}

export class DifferentUTCOffsetsNotification extends GeneralWarningNotification {
  constructor(user, currentTimezone) {
    const timezoneDetails = user.profile.timezone
      ? user.profile.timezone.utc_offset + ", " + user.profile.timezone.id
      : "no timezone info in user’s profile";
    const currentTimezoneDetails = currentTimezone
      ? currentTimezone.utc_offset + ", " + currentTimezone.id
      : "current timezone info is not set";

    super(
      getGeneralWarningDefaultPretext(user),
      `${user.profile.first_name}’s current utc offset (${currentTimezoneDetails}) is not the same as saved on the server (${timezoneDetails}). It can cause errors in calendar and confusions during booking/rescheduling sessions.`,
      null,
      [new IntercomSlackAction(user.id, user.profile.first_name)]
    );
  }
}

export class TimeDifferenceNotification extends GeneralWarningNotification {
  constructor(user, offset) {
    const offsetInMin = parseInt(Math.abs(offset) / (60 * 1000), 10);
    const clockDirection = offset > 0 ? "behind" : "ahead";

    super(
      getGeneralWarningDefaultPretext(user),
      `${user.profile.first_name}’s local time is different from the server time. User’s clock is ${clockDirection} the server’s, the difference is about ${offsetInMin} minutes. It can be confusing for this user that lesson starts earlier or later then he/she expected.`,
      null,
      [new IntercomSlackAction(user.id, user.profile.first_name)]
    );
  }
}

export class BothConnectedNotification extends SessionSuccessNotification {
  constructor(lesson) {
    const users = getUsersDetails(lesson);
    const lessonEvent = {
      lessonId: lesson.id,
      eventType: LessonSessionEventTypes.SUCCESS_USER_BOTH_CONNECTED,
      platform: lesson.platform,
    };

    super(
      getSessionInfoDefaultPretext(lesson),
      `${users.user.profile.first_name} (${users.user.role}) and ${users.other.profile.first_name} (${users.other.role}) are connected.`,
      null,
      getDefaultSessionNotificationActions(lesson),
      lessonEvent
    );
  }
}
